// PortfolioStatsPanel — expanderbar stats-vy under en portfolio-card i listan.
//
// Visar:
//   - Tidsperiod-chips: 7d / 30d / All time
//   - Totals: total views, unique visitors, avg views/visitor
//   - Sparkline över dagliga views i perioden
//   - Top 5 viewed images (med tumnaglar om vi har dem cache:de — annars id:n)
//   - Scroll-depth-staplar (25/50/75/100%)
//   - Contact clicks-breakdown (email/phone/instagram)
//   - Reset-knapp som rensar portfolio_views + sätter stats_reset_at
//
// All data kommer från /api/portfolios/:id/stats/full (Umami-proxy via backend).
// Owner-traffic (inloggade workspace-medlemmar) räknas inte med — se
// trackPortfolio()-wrapper i analytics.ts.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { RotateCcw } from 'lucide-react';
import {
  getPortfolioStatsFull,
  resetPortfolioViews,
  type StatsRange,
  type PortfolioStatsFull,
} from '../../api/portfolios';

interface Props {
  portfolioId: string;
}

export function PortfolioStatsPanel({ portfolioId }: Props) {
  const queryClient = useQueryClient();
  const [range, setRange] = useState<StatsRange>('7d');

  const statsQuery = useQuery({
    queryKey: ['portfolio-stats-full', portfolioId, range],
    queryFn: () => getPortfolioStatsFull(portfolioId, range),
    staleTime: 60_000, // cacha 1 minut — stats förändras inte i realtid ändå
  });

  const resetMutation = useMutation({
    mutationFn: () => resetPortfolioViews(portfolioId),
    onSuccess: () => {
      // Invalidera alla stats-queries för denna portfolio (alla ranges)
      queryClient.invalidateQueries({ queryKey: ['portfolio-stats-full', portfolioId] });
      queryClient.invalidateQueries({ queryKey: ['portfolios'] });
    },
  });

  function handleReset() {
    const ok = window.confirm(
      'Reset stats for this portfolio?\n\nThis will delete all recorded views and ' +
        'start counting from now. Useful when you’ve tested the link yourself and want ' +
        'a clean baseline before sharing with agencies.\n\nThis cannot be undone.',
    );
    if (ok) resetMutation.mutate();
  }

  if (statsQuery.isLoading) {
    return (
      <div className="mt-1 pt-5 border-t border-loupe-border animate-pulse">
        <div className="h-3 w-32 bg-loupe-bg-3 rounded mb-4" />
        <div className="h-16 bg-loupe-bg-3 rounded mb-4" />
        <div className="h-24 bg-loupe-bg-3 rounded" />
      </div>
    );
  }

  if (statsQuery.error) {
    return (
      <div className="mt-1 pt-5 border-t border-loupe-border text-xs text-michelle">
        Could not load stats. {(statsQuery.error as Error).message}
      </div>
    );
  }

  const stats = statsQuery.data;
  if (!stats) return null;

  return (
    <div className="mt-1 pt-5 border-t border-loupe-border flex flex-col gap-5">
      {/* Range chips + reset */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          {(['7d', '30d', 'all'] as StatsRange[]).map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRange(r)}
              className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-wider transition-colors ${
                range === r
                  ? 'bg-white text-black'
                  : 'bg-transparent text-loupe-text-3 hover:text-loupe-text border border-loupe-border'
              }`}
            >
              {r === 'all' ? 'All time' : r}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={handleReset}
          disabled={resetMutation.isPending}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[10px] uppercase tracking-wider text-loupe-text-3 hover:text-michelle border border-loupe-border hover:border-michelle/40 transition-colors disabled:opacity-50"
          title="Reset stats — useful after own testing"
        >
          <RotateCcw className="w-3 h-3" />
          {resetMutation.isPending ? 'Resetting…' : 'Reset'}
        </button>
      </div>

      {/* Topp-tal */}
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Total views" value={stats.totals.totalViews} />
        <Stat label="Unique visitors" value={stats.totals.uniqueVisitors} />
        <Stat
          label="Views / visitor"
          value={
            stats.totals.avgViewsPerVisitor > 0
              ? stats.totals.avgViewsPerVisitor.toFixed(1)
              : '—'
          }
        />
      </div>

      {/* Sparkline */}
      {stats.dailySeries.length > 1 ? (
        <Sparkline series={stats.dailySeries} />
      ) : null}

      {/* Top images + scroll depth */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <ScrollDepth counts={stats.totals.scrollDepthCounts} />
        <TopImages items={stats.topImages.slice(0, 5)} />
      </div>

      {/* Contact clicks */}
      {(stats.contactClicks.email > 0 ||
        stats.contactClicks.phone > 0 ||
        stats.contactClicks.instagram > 0) ? (
        <ContactClicks clicks={stats.contactClicks} />
      ) : null}

      {/* Empty hint */}
      {stats.totals.totalViews === 0 ? (
        <div className="text-[11px] text-loupe-text-3 italic">
          No external visits yet in this range. Your own (logged-in) views are excluded.
        </div>
      ) : null}
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="px-3 py-3 bg-loupe-bg-3 border border-loupe-border rounded-lg">
      <div className="text-[9px] text-loupe-text-3 tracking-[0.18em] uppercase mb-1">
        {label}
      </div>
      <div className="font-display text-2xl tabular-nums tracking-tight text-loupe-text">
        {value}
      </div>
    </div>
  );
}

function Sparkline({ series }: { series: PortfolioStatsFull['dailySeries'] }) {
  const max = Math.max(...series.map((d) => d.views), 1);
  return (
    <div className="flex items-end gap-1 h-10">
      {series.map((d) => {
        const h = Math.max(2, (d.views / max) * 40);
        return (
          <div
            key={d.day}
            className="flex-1 bg-loupe-text-3/40 rounded-sm transition-colors hover:bg-loupe-text-2"
            style={{ height: `${h}px` }}
            title={`${d.day}: ${d.views} views`}
          />
        );
      })}
    </div>
  );
}

function ScrollDepth({
  counts,
}: {
  counts: PortfolioStatsFull['totals']['scrollDepthCounts'];
}) {
  // Räkna max över depths för relativa staplar
  const max = Math.max(counts['0.25'], counts['0.5'], counts['0.75'], counts['1'], 1);
  const rows: Array<{ pct: string; n: number }> = [
    { pct: '25%', n: counts['0.25'] },
    { pct: '50%', n: counts['0.5'] },
    { pct: '75%', n: counts['0.75'] },
    { pct: '100%', n: counts['1'] },
  ];
  return (
    <div>
      <div className="text-[9px] text-loupe-text-3 tracking-[0.18em] uppercase mb-2">
        Scroll depth
      </div>
      <div className="flex flex-col gap-1.5">
        {rows.map((r) => (
          <div key={r.pct} className="flex items-center gap-2 text-[11px]">
            <span className="w-9 text-loupe-text-3 tabular-nums">{r.pct}</span>
            <div className="flex-1 h-2 bg-loupe-bg-3 rounded-full overflow-hidden">
              <div
                className="h-full bg-loupe-text-3/50"
                style={{ width: `${(r.n / max) * 100}%` }}
              />
            </div>
            <span className="w-7 text-right text-loupe-text-2 tabular-nums">{r.n}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TopImages({
  items,
}: {
  items: Array<{ imageId: string; views: number }>;
}) {
  return (
    <div>
      <div className="text-[9px] text-loupe-text-3 tracking-[0.18em] uppercase mb-2">
        Top images
      </div>
      {items.length === 0 ? (
        <div className="text-[11px] text-loupe-text-3 italic py-1">
          No image views yet
        </div>
      ) : (
        <ol className="flex flex-col gap-1 text-[11px]">
          {items.map((t, i) => (
            <li
              key={t.imageId}
              className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-loupe-bg-3/40"
            >
              <span className="flex items-center gap-2 truncate">
                <span className="text-loupe-text-3 tabular-nums w-4">{i + 1}.</span>
                <span className="font-mono text-loupe-text-2 truncate">
                  {t.imageId.slice(-8)}
                </span>
              </span>
              <span className="text-loupe-text-2 tabular-nums">{t.views}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function ContactClicks({
  clicks,
}: {
  clicks: PortfolioStatsFull['contactClicks'];
}) {
  return (
    <div>
      <div className="text-[9px] text-loupe-text-3 tracking-[0.18em] uppercase mb-2">
        Contact clicks
      </div>
      <div className="flex flex-wrap gap-2 text-[11px]">
        {clicks.email > 0 ? (
          <span className="px-2 py-1 rounded bg-loupe-bg-3 text-loupe-text-2">
            email · <span className="tabular-nums">{clicks.email}</span>
          </span>
        ) : null}
        {clicks.instagram > 0 ? (
          <span className="px-2 py-1 rounded bg-loupe-bg-3 text-loupe-text-2">
            instagram · <span className="tabular-nums">{clicks.instagram}</span>
          </span>
        ) : null}
        {clicks.phone > 0 ? (
          <span className="px-2 py-1 rounded bg-loupe-bg-3 text-loupe-text-2">
            phone · <span className="tabular-nums">{clicks.phone}</span>
          </span>
        ) : null}
      </div>
    </div>
  );
}
